if (!customElements.get('the-fold-carousel')) {
  customElements.define('the-fold-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.querySelectorAll('[data-carousel-clone]').forEach((slide) => slide.remove());
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      if (!this.viewport || !this.track || !this.slides.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.index = matchMedia('(max-width: 575px)').matches ? this.slides.length - 1 : 0;
      this.position = this.slides.length + this.index;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.slides.forEach((slide, index) => { slide.dataset.carouselIndex = index; });

      for (const side of ['before', 'after']) {
        const fragment = document.createDocumentFragment();
        for (const slide of this.slides) {
          const clone = slide.cloneNode(true);
          clone.setAttribute('data-carousel-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          clone.removeAttribute('data-shopify-editor-block');
          fragment.append(clone);
        }
        if (side === 'before') this.track.prepend(fragment);
        else this.track.append(fragment);
      }
      this.dataset.ready = 'true';
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), { signal }));
      this.viewport.addEventListener('keydown', (event) => {
        const directions = { ArrowLeft: -1, ArrowRight: 1 };
        if (event.key in directions) {
          event.preventDefault();
          this.goTo(this.index + directions[event.key]);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.slides.length - 1);
        }
      }, { signal });
      this.viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        this.normalize();
        this.drag = { id: event.pointerId, start: event.clientX, delta: 0, time: performance.now() };
        this.viewport.setPointerCapture(event.pointerId);
      }, { signal });
      this.viewport.addEventListener('pointermove', (event) => {
        if (this.drag?.id !== event.pointerId) return;
        this.drag.delta = event.clientX - this.drag.start;
        this.render(false, this.drag.delta);
      }, { signal });
      for (const type of ['pointerup', 'pointercancel']) {
        this.viewport.addEventListener(type, (event) => {
          if (this.drag?.id !== event.pointerId) return;
          const delta = this.drag.delta;
          const duration = performance.now() - this.drag.time;
          this.drag = null;
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          const distance = Math.abs(delta) / this.stride;
          const steps = type === 'pointercancel' || Math.abs(delta) <= 5 ? 0 : Math.sign(-delta) * (duration > 300 ? Math.round(distance) : Math.ceil(distance));
          this.goTo(this.index + steps);
        }, { signal });
      }
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.normalize();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-carousel-slide]'));
        if (index >= 0) this.goTo(index);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.normalize());
      this.resizeObserver.observe(this.viewport);
      this.render(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    normalize() {
      this.position = this.slides.length + this.index;
      this.render(false);
    }

    goTo(index) {
      if (this.position < this.slides.length || this.position >= this.slides.length * 2) {
        this.normalize();
        this.track.getBoundingClientRect();
      }
      const target = Math.max(-this.slides.length, Math.min(this.slides.length * 2 - 1, index));
      this.position = this.slides.length + target;
      this.index = ((target % this.slides.length) + this.slides.length) % this.slides.length;
      this.render(!this.reducedMotion.matches);
      if (this.reducedMotion.matches) this.normalize();
    }

    render(animate, delta = 0) {
      this.stride = this.slides[0].getBoundingClientRect().width;
      const offset = (this.viewport.clientWidth - this.stride) / 2 - this.position * this.stride + delta;
      this.track.style.transition = animate ? 'transform 250ms ease' : 'none';
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
      for (const slide of this.track.children) {
        slide.toggleAttribute('data-current', Number(slide.dataset.carouselIndex) === this.index);
      }
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  });
}
